import {
  useDaily,
  useParticipantIds,
  DailyAudio,
} from "@daily-co/daily-react";
import { useEffect, useRef, useState, useCallback } from "react";
import { ConversationType } from "../../utils/avatar_api";
import { Box, IconButton, Tooltip } from "@mui/material";
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import CloseIcon from '@mui/icons-material/Close';
import AvatarVideo from "./avatarVideo";
import { logging } from "../../utils/logger";
import { useNavigate, useParams } from "react-router-dom";

interface AvatarInterfaceProps {
  conversation: ConversationType | null;
}

const AvatarInterface: React.FC<AvatarInterfaceProps> = (props) => {
  const { conversation } = props;
  // Start muted — mic is enabled only after the remote avatar stream is present
  // to prevent the avatar's own audio from echoing back through the mic.
  const [isMuted, setIsMuted] = useState(true);
  const micEnabledAfterJoin = useRef(false);
  const daily = useDaily();
  const navigate = useNavigate();
  const { userName } = useParams();

  const remoteParticipantIds = useParticipantIds({
    filter: "remote", sort: "joined_at"
  });

  useEffect(() => {
    if (conversation?.conversation_url) {
      // Reset mic gate for this new conversation session
      micEnabledAfterJoin.current = false;

      daily?.join({
        url: conversation.conversation_url,
        startVideoOff: true,
        // Start with mic OFF — we enable it only once the remote avatar stream
        // is present so the browser's AEC has the reference signal it needs
        // before the microphone opens.
        startAudioOff: true,
      }).catch((error) => {
        logging.error("error in daily: ", { context: error })
      })

      return () => {
        daily?.leave()
      }
    }
  }, [conversation?.conversation_url, daily])

  // Enable mic only after the remote participant (avatar) has joined.
  // This gives the browser echo-cancellation subsystem the playback
  // reference it needs before we open the microphone.
  // Fallback: if the avatar stream never arrives within 5 s, enable mic anyway
  // so the user is never permanently muted.
  useEffect(() => {
    if (!micEnabledAfterJoin.current && remoteParticipantIds.length > 0) {
      micEnabledAfterJoin.current = true;
      daily?.setLocalAudio(true);
      setIsMuted(false);
    }
  }, [remoteParticipantIds, daily]);

  useEffect(() => {
    if (!conversation?.conversation_url) return;
    const fallback = setTimeout(() => {
      if (!micEnabledAfterJoin.current) {
        micEnabledAfterJoin.current = true;
        daily?.setLocalAudio(true);
        setIsMuted(false);
      }
    }, 5000);
    return () => clearTimeout(fallback);
  }, [conversation?.conversation_url, daily]);

  const toggleMic = useCallback(() => {
    const nextMuted = !isMuted;
    daily?.setLocalAudio(!nextMuted);
    setIsMuted(nextMuted);
  }, [daily, isMuted]);

  const handleExit = useCallback(() => {
    daily?.leave();
    if (userName) {
      navigate(`/${userName}/chat`);
    } else {
      navigate(-1);
    }
  }, [daily, navigate, userName]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        bgcolor: '#000', // Black background for a clean "video" look
      }}
    >
      {/* Action Controls Overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 40,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 3,
          zIndex: 10,
          bgcolor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          p: 2,
          borderRadius: 10,
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <Tooltip title={isMuted ? "Unmute Mic" : "Mute Mic"}>
          <IconButton
            onClick={toggleMic}
            sx={{
              bgcolor: isMuted ? 'error.main' : 'rgba(255, 255, 255, 0.2)',
              color: '#fff',
              '&:hover': {
                bgcolor: isMuted ? 'error.dark' : 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            {isMuted ? <MicOffIcon /> : <MicIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Exit Room">
          <IconButton
            onClick={handleExit}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: '#fff',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {
          remoteParticipantIds?.length > 0 &&
          remoteParticipantIds.map((val, ind) => {
            return (
              <AvatarVideo
                key={`Avatar-${val}`}
                id={val}
              />
            )
          })
        }
        {/* Audio for remote participants */}
        <DailyAudio />
      </Box>
    </Box>
  );
}

export default AvatarInterface;
